export class DateHelper extends Date {
  public constructor(date?: Date | DateHelper | number | string) {
    if (date) {
      if (typeof date === 'number' && Math.abs(Date.now() - date) > Math.abs(Date.now() - date * 1000)) {
        date = date * 1000; // if timestamp is in seconds, send it in milliseconds to the Date constructor (can't send it in seconds)
      }
      super(date);
    } else {
      super();
    }
  }
  public static readonly MINUTE = 60;
  public static readonly HOUR = 60 * DateHelper.MINUTE;
  public static readonly DAY = 24 * DateHelper.HOUR;

  public static toMs(unix: number): number {
    return unix * 1000;
  }

  public static fromMs(unix: number): number {
    return unix / 1000;
  }

  public static readonly DAY_BY_MONTH = 365.25 / 12; // We consider that a month has 365.25/12 days to take into account leap years
  public static readonly toMonths = (seconds: number) =>
    Math.round(seconds / (3600 * 24 * DateHelper.DAY_BY_MONTH));
  public static readonly monthsToSeconds = (months: number) =>
    Math.round(months * 3600 * 24 * DateHelper.DAY_BY_MONTH);
  public static readonly toDays = (seconds: number) => seconds / (3600 * 24);
  public static readonly daysToSeconds = (days: number) => Math.round(days * 3600 * 24);
  public static readonly toHours = (seconds: number) => seconds / 3600;

  /**
   * Returns the number of days between two timestamps (seconds or milliseconds).
   * @param fromTimestamp - Start timestamp.
   * @param toTimestamp - End timestamp.
   * @param options.absolute - Return absolute value (default: `true`). Set to `false` for a signed result.
   * @param options.inclusive - Include both endpoints in the count, i.e. adds 1 (default: `false`).
   */
  public static daysBetweenTimestamps(
    fromTimestamp: number,
    toTimestamp: number,
    options: { absolute?: boolean; inclusive?: boolean } = { absolute: true, inclusive: false }
  ): number {
    const toMs = (ts: number) =>
      Math.abs(DateHelper.now() - ts) > Math.abs(DateHelper.now() - ts * 1000) ? ts * 1000 : ts;

    const fromDay = new DateHelper(toMs(fromTimestamp)).startOfDay().getTime();
    const toDay = new DateHelper(toMs(toTimestamp)).startOfDay().getTime();

    const diff = Math.round((toDay - fromDay) / (1000 * 60 * 60 * 24));
    const result = (options.absolute ?? true) ? Math.abs(diff) : diff;
    return (options.inclusive ?? false) ? result + 1 : result;
  }

  public getDayOfWeek(): number {
    const day = this.getDay();
    if (day === 0) {
      return 6; // dimanche
    }

    return day - 1;
  }

  // set the hour to the beginning of the day
  public startOfDay(): DateHelper {
    const today = new DateHelper(this);
    today.setHours(0, 0, 0, 0);
    return today;
  }

  // set the hour to the end of the day
  public endOfDay(): DateHelper {
    const newDate = new DateHelper(this);
    newDate.setHours(23, 59, 59, 59);
    return newDate;
  }

  // substract some days to the current date
  public subtractDays(days: number): DateHelper {
    const current = new DateHelper(this);
    current.setDate(current.getDate() - days);
    return current;
  }

  public clearMinutesAndSeconds(): DateHelper {
    const current = new DateHelper(this);
    current.setMinutes(0, 0, 0);
    return current;
  }

  // add some minutes on the current date
  public addMinutes(minutes: number): DateHelper {
    const current = new DateHelper(this);
    current.setMinutes(current.getMinutes() + minutes);
    return current;
  }

  // subtract some minutes on the current date
  public subtractMinutes(minutes: number): DateHelper {
    const current = new DateHelper(this);
    current.setMinutes(current.getMinutes() - minutes);
    return current;
  }

  // add some hours on the current date
  public addHours(hour: number): DateHelper {
    const current = new DateHelper(this);
    current.setHours(current.getHours() + hour);
    return current;
  }

  // subtract some hours on the current date
  public subtractHours(hour: number): DateHelper {
    const current = new DateHelper(this);
    current.setHours(current.getHours() - hour);
    return current;
  }

  // add some days on the current date
  public addDays(days: number): DateHelper {
    const current = new DateHelper(this);
    current.setDate(current.getDate() + days);
    return current;
  }

  // get the interval between two dates
  public timeInterval(date: DateHelper): number {
    return date.getTime() - new DateHelper(this).getTime();
  }

  public getTimestamp(): number {
    return Math.trunc(this.getTime() / 1000);
  }

  // convert a unix from milliseconds to seconds
  public unixToSeconds(unix: number): number {
    return unix / 1000;
  }

  // turn a date into a seconds timestamp
  public timestamp(): number {
    return Math.trunc(this.getTime() / 1000);
  }

  // get last month date from today
  public lastMonth(): DateHelper {
    const current = new DateHelper(this);
    return new DateHelper(current.setMonth(current.getMonth() - 1));
  }

  public lastYear(): DateHelper {
    const current = new DateHelper(this);
    return new DateHelper(
      current.setFullYear(current.getFullYear() - 1, current.getMonth(), current.getDate())
    );
  }

  public nextYear(): DateHelper {
    const current = new DateHelper(this);
    return new DateHelper(
      current.setFullYear(current.getFullYear() + 1, current.getMonth(), current.getDate())
    );
  }

  public startOfMonth(): DateHelper {
    return new DateHelper(this.setDate(1));
  }

  // substract some month to the current date
  public subtractMonths(months: number): DateHelper {
    return new DateHelper(this.setMonth(this.getMonth() - months));
  }

  // substract some month to the current date
  public addMonths(months: number): DateHelper {
    return new DateHelper(this.setMonth(this.getMonth() + months));
  }

  //TODO: à généraliser
  /**
   * Converts a date string in French format (DD/MM/YYYY) and a time string (H:mm or HH:mm)
   * into a UNIX timestamp in seconds.
   *
   * @param dateString - A string representing the date in "DD/MM/YYYY" format.
   * @param timeString - A string representing the time in "H:mm" or "HH:mm" format.
   * @returns A UNIX timestamp (in seconds) representing the combined date and time.
   */
  public convertToTimestamp(dateString: string, timeString: string) {
    const [day, month, year] = dateString.split('/');

    // Split the time into hour, minute, and optional second
    const timeParts = timeString.split(':');
    const hour = timeParts[0]?.padStart(2, '0') ?? '00';
    const minute = timeParts[1]?.padStart(2, '0') ?? '00';
    const second = timeParts[2]?.padStart(2, '0') ?? '00';

    const timeISO = `${hour}:${minute}:${second}`;

    // Build ISO date string
    const dateISO = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${timeISO}`;

    const date = new DateHelper(dateISO);

    return date.timestamp();
  }
}
